import { getPathInfo } from '../../core/pathRemapping';
import { match } from 'path-to-regexp';

/**
 * Extract params from a URL path using a pattern via path-to-regexp
 */
function extractMatchParams<T>(pattern: string, path: string): Partial<T> | null {
    try {
        const matchFn = match(pattern, { decode: decodeURIComponent });
        const result = matchFn(path);
        if (result && typeof result !== 'boolean') {
            return result.params as Partial<T>;
        }
    } catch { /* ignore */ }
    return null;
}

/**
 * Hook to extract URL parameters that works with both remapped and non-remapped paths.
 * Router-agnostic: uses window.location + path-to-regexp directly (no react-router-dom needed).
 * 
 * @param internalPath - The internal path pattern (e.g., "/hello-with-param/:myparam")
 * @returns Parameters object with extracted values
 * 
 * @example
 * ```tsx
 * function HelloWithParamPage() {
 *   // Works for both /hello-with-param/test AND /myremap/test
 *   const { myparam } = useRemappableParams<{ myparam: string }>('/hello-with-param/:myparam');
 *   return <div>Parameter: {myparam}</div>;
 * }
 * ```
 */
export function useRemappableParams<T extends Record<string, string>>(
    internalPath: string
): Partial<T> {
    const pathInfo = getPathInfo();
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

    // If not remapped, extract params from URL using the internal pattern directly
    if (!pathInfo.isRemapped) {
        return extractMatchParams<T>(internalPath, currentPath) ?? {};
    }

    // If remapped, extract params from the external URL
    try {
        let externalPattern: string | null = null;

        // Check localStorage for explicit remapping (development/testing)
        if (typeof localStorage !== 'undefined') {
            try {
                const remapData = localStorage.getItem('tagadapay-remap');
                if (remapData) {
                    const parsed = JSON.parse(remapData);
                    if (parsed.externalPath && parsed.internalPath === internalPath) {
                        externalPattern = parsed.externalPath;
                    }
                }
            } catch (e) {
                // Ignore parsing errors
            }
        }

        // Check meta tag for production remapping
        if (!externalPattern && typeof document !== 'undefined') {
            const metaTag = document.querySelector('meta[name="tagadapay-path-remap-pattern"]');
            if (metaTag) {
                externalPattern = metaTag.getAttribute('content');
            }
        }

        // If we have an external pattern, extract params and map to internal names
        if (externalPattern) {
            const matchFn = match(externalPattern, { decode: decodeURIComponent });
            const result = matchFn(pathInfo.externalPath);

            if (result && typeof result !== 'boolean') {
                const externalParamNames = extractParamNames(externalPattern);
                const internalParamNames = extractParamNames(internalPath);

                const mappedParams: Partial<T> = {};
                externalParamNames.forEach((externalName, index) => {
                    const internalName = internalParamNames[index];
                    if (internalName && result.params[externalName] !== undefined) {
                        (mappedParams as any)[internalName] = result.params[externalName];
                    }
                });

                return mappedParams;
            }
        }

        // Fallback: try to extract from internal path directly
        return extractMatchParams<T>(internalPath, pathInfo.externalPath) ?? {};
    } catch (error) {
        console.error('[useRemappableParams] Failed to extract params:', error);
    }

    // Final fallback: try matching current URL against internal pattern
    return extractMatchParams<T>(internalPath, currentPath) ?? {};
}

/**
 * Extract parameter names from a path pattern
 * @param pattern - Path pattern like "/hello/:param1/:param2"
 * @returns Array of parameter names like ["param1", "param2"]
 */
function extractParamNames(pattern: string): string[] {
    const paramRegex = /:([^/]+)/g;
    const matches = [];
    let match;

    while ((match = paramRegex.exec(pattern)) !== null) {
        matches.push(match[1]);
    }

    return matches;
}

